import { useState, type ReactNode } from 'react';
import { UMIcon } from './UMIcon';
import { UMR } from './referenceTokens';

export interface UMSidePanelProps {
  title: string;
  onClose?: () => void;
  children: ReactNode;
  /** Production panels are 300px. Detail panels on the right may be wider. */
  width?: number;
  side?: 'left' | 'right';
  footer?: ReactNode;
  /** Grey `history` tab hanging on the right edge (Export / Import: "Voir plus d'informations"). */
  onHistory?: () => void;
}

/** Docked panel opened from the rail (left) or for a selected object (right). */
export function UMSidePanel({ title, onClose, children, width = UMR.size.panelWidth, side = 'left', footer, onHistory }: UMSidePanelProps) {
  return (
    <aside
      aria-label={title}
      style={{
        position: 'relative',
        width,
        minWidth: width,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: UMR.white,
        borderRight: side === 'left' ? `1px solid ${UMR.border}` : undefined,
        borderLeft: side === 'right' ? `1px solid ${UMR.border}` : undefined,
        fontFamily: UMR.font,
        fontSize: 14,
        color: UMR.black,
        boxSizing: 'border-box',
      }}
    >
      {onHistory && (
        <button
          type="button"
          title="Voir plus d'informations"
          aria-label="Voir plus d'informations"
          onClick={onHistory}
          style={{
            position: 'absolute', top: 16, right: -32, width: 32, height: 32, border: 'none', padding: 0,
            borderRadius: '0 4px 4px 0', background: UMR.borderInput, cursor: 'pointer',
            fontFamily: UMR.iconFont, fontSize: 20, lineHeight: '32px', textAlign: 'center', color: UMR.black,
          }}
        >
          history
        </button>
      )}
      <header
        role="heading"
        aria-level={2}
        style={{
          height: UMR.size.panelHeaderHeight,
          minHeight: UMR.size.panelHeaderHeight,
          boxSizing: 'border-box',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${UMR.borderSubtle}`,
          fontSize: 18,
          fontWeight: 600,
          lineHeight: '28px',
        }}
      >
        {title}
        {onClose && (
          <button
            type="button"
            aria-label="Fermer"
            onClick={onClose}
            style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', display: 'flex' }}
          >
            <UMIcon name="close" size={16} weight={700} color={UMR.textMuted} />
          </button>
        )}
      </header>
      <div className="um-side-panel-body" style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
        <style>{'.um-side-panel-body > .um-layer-group:last-child{border-bottom:none;padding-bottom:0;margin-bottom:0}'}</style>
        {children}
      </div>
      {footer && (
        <div style={{ borderTop: `1px solid ${UMR.borderSubtle}`, padding: 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {footer}
        </div>
      )}
    </aside>
  );
}

/** Fluid v5 checkbox as rendered in production (16px square, 2px border, blue-600 when checked/indeterminate). */
export function UMCheckbox({
  checked,
  onChange,
  label,
  height = 24,
  indeterminate = false,
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  height?: number;
  indeterminate?: boolean;
  disabled?: boolean;
}) {
  const filled = checked || indeterminate;
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, lineHeight: '24px', height, cursor: disabled ? 'not-allowed' : 'pointer', flex: 1, minWidth: 0 }}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-checked={indeterminate ? 'mixed' : checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0, margin: 0 }}
      />
      <span
        aria-hidden="true"
        style={{
          width: 16,
          height: 16,
          boxSizing: 'border-box',
          flexShrink: 0,
          border: `2px solid ${filled ? UMR.logoText : UMR.checkboxBorder}`,
          background: filled ? UMR.logoText : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {indeterminate ? (
          <span style={{ width: 8, height: 2, background: UMR.white }} />
        ) : (
          checked && <UMIcon name="check" size={12} weight={700} color={UMR.white} />
        )}
      </span>
      {label}
    </label>
  );
}

export interface UMLayerGroupProps {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  defaultOpen?: boolean;
  /** Shows the `more_vert` action (production: "Modifier les légendes"). */
  onMenu?: () => void;
  /** Controls above the legend (e.g. <UMTag>Conso. chaud</UMTag>). */
  tag?: ReactNode;
  /** Legend content, normally a list of <UMLegendItem />. */
  children?: ReactNode;
}

/** One `layer-form` group of the "Couches de données" panel (measured structure). */
export function UMLayerGroup({ label, checked, onCheckedChange, defaultOpen = false, onMenu, tag, children }: UMLayerGroupProps) {
  const [open, setOpen] = useState(defaultOpen);
  const hasContent = Boolean(tag || children);
  return (
    <section className="um-layer-group" style={{ borderBottom: `1px solid ${UMR.borderSubtle}`, paddingBottom: 16, marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: onMenu ? 'flex-end' : 'center', gap: 4 }}>
        <button
          type="button"
          aria-label={open ? `Replier ${label}` : `Déplier ${label}`}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', display: 'flex' }}
        >
          <UMIcon name={open ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} size={18} box={24} color={UMR.black} />
        </button>
        <UMCheckbox checked={checked} onChange={onCheckedChange} label={label} height={onMenu ? 26 : 24} />
        {/* production keeps an empty action slot: the label ends 4px before the panel padding */}
        {!onMenu && <span style={{ width: 0 }} />}
        {onMenu && (
          <button
            type="button"
            aria-label={`Options ${label}`}
            onClick={onMenu}
            style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', display: 'flex', alignSelf: 'flex-start' }}
          >
            <UMIcon name="more_vert" size={18} box={24} color={UMR.black} />
          </button>
        )}
      </div>
      {open && hasContent && (
        <div style={{ margin: '4px 0 0 28px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {tag && <div style={{ display: 'flex' }}>{tag}</div>}
          {children && <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>{children}</div>}
        </div>
      )}
    </section>
  );
}

export interface UMLegendItemProps {
  label: string;
  color: string;
  /** fill = 20×13 swatch, line = 20×4 bar, visibility = eye toggle (Réseaux énergétiques). */
  kind?: 'fill' | 'line' | 'visibility';
  /** Greyed out (opacity .5) — production shows hidden sub-layers this way. */
  disabled?: boolean;
  /** Indented sub-legend (24px), e.g. "Réseaux existants" under "Canalisations RCU/RFU". */
  sub?: boolean;
  onClick?: () => void;
}

/** Legend row: swatch/line/eye in a 20×13 box + 12px description (gap 4). */
export function UMLegendItem({ label, color, kind = 'fill', disabled, sub, onClick }: UMLegendItemProps) {
  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, lineHeight: '18px', color: UMR.black,
        marginLeft: sub ? 24 : 0, opacity: disabled ? 0.5 : 1, cursor: onClick ? 'pointer' : 'default',
      }}
    >
      {kind === 'visibility' ? (
        <UMIcon name="visibility" family="icons" size={18} color={color} style={{ width: 20, height: 13, overflow: 'visible' }} />
      ) : (
        <span aria-hidden="true" style={{ width: 20, height: 13, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <span style={{ width: 20, height: kind === 'line' ? 4 : 13, background: color }} />
        </span>
      )}
      <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
    </div>
  );
}

/** Grey Fluid v5 tag-select used in layer groups (e.g. "Conso. chaud ▾"). */
export function UMTag({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, height: 26, boxSizing: 'border-box', padding: 4,
        background: UMR.tagBg, border: `1px solid ${UMR.borderInput}`, borderRadius: 0,
        fontFamily: UMR.font, fontSize: 14, lineHeight: '16px', color: UMR.black, cursor: 'pointer',
      }}
    >
      <span>{children}</span>
      <UMIcon name="arrow_drop_down" family="icons" size={16} color={UMR.black} />
    </button>
  );
}
